import React, { useState, useEffect } from 'react';
import {
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Alert,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Checkbox,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../utills/axiosInstance';
import moment from 'moment';
import { usePermissions } from '../utills/usePermissions';

const PayRun = () => {
  const [payRuns, setPayRuns] = useState([]);
  const [month, setMonth] = useState(moment().month() + 1);
  const [year, setYear] = useState(moment().year());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [employeesPreview, setEmployeesPreview] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const [grandNetPay, setGrandNetPay] = useState(0);
  const [grandEmployerCost, setGrandEmployerCost] = useState(0);
  const [grandTotalPay, setGrandTotalPay] = useState(0);
  const [employeeCount, setEmployeeCount] = useState(0);

  const { hasPermission } = usePermissions();

  useEffect(() => {
    fetchPayRuns();
  }, []);

  const fetchPayRuns = async () => {
    try {
      const res = await axiosInstance.get('/api/payroll/payruns');
      setPayRuns(res.data || []);
    } catch (err) {
      setError('Failed to load pay runs');
    }
  };

  const handleCreatePayRun = async () => {
    if (!month || !year) {
      setError('Month and year required');
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/payroll/payrun/preview?month=${month}&year=${year}`);
      setEmployeesPreview(res.data.employees || []);
      setCreateDialog(true);
      setSelectedEmployees([]);
      setGrandNetPay(0);
      setGrandEmployerCost(0);
      setGrandTotalPay(0);
      setEmployeeCount(0);
      setError('');
      setSuccess('');
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      employeesPreview.forEach(emp => {
        if (!selectedEmployees.includes(emp._id)) {
          handleEmployeeSelect(emp, true);
        }
      });
    } else {
      setSelectedEmployees([]);
      setGrandNetPay(0);
      setGrandEmployerCost(0);
      setGrandTotalPay(0);
      setEmployeeCount(0);
    }
  };

  const handleEmployeeSelect = (emp, checked) => {
    const netPay = emp.netPay || 0;
    const employerCost = (emp.employerContributions || 0);
    const totalPay = netPay + emp.allowance - emp.lopDed;
    const grandTotal = totalPay + employerCost;

    if (checked) {
      setSelectedEmployees(prev => [...prev, emp._id]);
      setGrandNetPay(prev => prev + totalPay);
      setGrandEmployerCost(prev => prev + employerCost);
      setGrandTotalPay(prev => prev + grandTotal);
      setEmployeeCount(prev => prev + 1);
    } else {
      setSelectedEmployees(prev => prev.filter(id => id !== emp._id));
      setGrandNetPay(prev => prev - totalPay);
      setGrandEmployerCost(prev => prev - employerCost);
      setGrandTotalPay(prev => prev - grandTotal);
      setEmployeeCount(prev => prev - 1);
    }
  };

  const confirmCreatePayRun = async () => {
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post('/api/payroll/payrun', {
        month,
        year,
        selectedEmployees,
        grossTotals: employeesPreview.filter(emp => selectedEmployees.includes(emp._id)).map(emp => emp.grossPay),
        netTotals: employeesPreview.filter(emp => selectedEmployees.includes(emp._id)).map(emp => emp.netPay)
      });

      setSuccess(`Pay run created for ${employeeCount} employees`);
      setTimeout(() => setSuccess(''), 5000);
      fetchPayRuns();
      setCreateDialog(false);
      setSelectedEmployees([]);
      setGrandNetPay(0);
      setGrandEmployerCost(0);
      setGrandTotalPay(0);
      setEmployeeCount(0);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to create pay run');
    } finally {
      setLoading(false);
    }
  };

  const processPayRun = async (id) => {
    setLoading(true);
    try {
      await axiosInstance.post(`/api/payroll/payrun/${id}/process`);
      setSuccess('Pay run processed');
      setTimeout(() => setSuccess(''), 3000);
      fetchPayRuns();
    } catch (err) {
      setError('Failed to process pay run');
    } finally {
      setLoading(false);
    }
  };

  const statusChip = (status) => {
    const colors = { draft: 'default', processing: 'warning', completed: 'info', paid: 'success' };
    return <Chip label={status} color={colors[status] || 'default'} size="small" />;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>New Pay Run</Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Month</InputLabel>
        <Select value={month} onChange={(e) => setMonth(e.target.value)}>
          {moment.months().map((m, i) => (
            <MenuItem key={i} value={i + 1}>{m}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField label="Year" type="number" fullWidth sx={{ mb: 2 }} value={year} onChange={(e) => setYear(e.target.value)} />

      <Button variant="contained" onClick={handleCreatePayRun} disabled={loading}>
        {loading ? <CircularProgress size={20} /> : 'Create Pay Run'}
      </Button>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>Existing Pay Runs</Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Month/Year</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Employees</TableCell>
            <TableCell>Total Gross</TableCell>
            <TableCell>Total Pay</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payRuns.map(p => (
            <TableRow key={p._id}>
              <TableCell>{moment().month(p.month - 1).format('MMMM')} {p.year}</TableCell>
              <TableCell>{statusChip(p.status)}</TableCell>
              <TableCell>{p.employeeCount || 0}</TableCell>
              <TableCell>₹{p.totalGross?.toFixed(0) || 0}</TableCell>
              <TableCell>₹{p.totalNet?.toFixed(0) || 0}</TableCell>
              <TableCell>
                {
                  hasPermission("payrun:process") && (
                    <Button onClick={() => processPayRun(p._id)} disabled={p.status !== 'draft'}>
                      Process
                    </Button>
                  )
                }
                {
                  !hasPermission("payrun:process") && (
                    <Typography>Need Permission to process</Typography>
                  )
                }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Select Employees for {moment.months()[month - 1]} {year} Pay Run
          <IconButton onClick={() => setCreateDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {/* NEW: Detailed Summary */}
          <Box sx={{ p: 3, backgroundColor: '#f0f7ff', borderBottom: 1 }}>
            <Typography variant="h6" gutterBottom>
              Selected {employeeCount} employees
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <Chip label={`Employee Take Home: ₹${grandNetPay.toFixed(0)}`} color="success" />
              <Chip label={`Employer Cost: ₹${grandEmployerCost.toFixed(0)}`} color="primary" />
              <Chip label={`Grand Total Pay: ₹${grandTotalPay.toFixed(0)}`} color="secondary" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Month: {moment.months()[month - 1]} {year}
            </Typography>
          </Box>

          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={employeeCount > 0 && employeeCount < employeesPreview.length}
                    checked={employeeCount === employeesPreview.length && employeesPreview.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Employee Name</TableCell>
                <TableCell align="right">Net Pay</TableCell>
                <TableCell align="right">Allowance</TableCell>
                <TableCell align="right">LOP Ded</TableCell>
                <TableCell align="right">Employer Cost</TableCell>
                <TableCell align="right"><strong style={{ color: "black" }}>Total Pay</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employeesPreview.map(emp => {
                const isSelected = selectedEmployees.includes(emp._id);
                const employerCost = emp.employerContributions || 0;
                const employeeAllowance = (emp.allowance || 0);
                const lopDed = emp.lopDed || 0;
                const totalPay = (emp.netPay || 0) + employeeAllowance - lopDed;
                const totalCTC = totalPay + employerCost;

                return (
                  <TableRow key={emp._id} hover selected={isSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleEmployeeSelect(emp, e.target.checked)}
                        disabled={loading}
                      />
                    </TableCell>
                    <TableCell>{emp.firstName} {emp.lastName}</TableCell>
                    <TableCell align="right">₹{(emp.netPay || 0).toFixed(0)}</TableCell>
                    <TableCell align="right">₹{employeeAllowance.toFixed(0)}</TableCell>
                    <TableCell align="right" style={{ color: 'red' }}>-₹{lopDed.toFixed(0)}</TableCell>
                    <TableCell align="right">₹{employerCost.toFixed(0)}</TableCell>
                    <TableCell align="right"><strong style={{ color: "black" }}>₹{totalCTC.toFixed(0)}</strong></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={confirmCreatePayRun}
            disabled={employeeCount === 0 || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm and Proceed'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PayRun;
